import {Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {InfluencerService} from '../../../services/influencer/influencer.service';
import {CommonConstant, InfluencerTabName, kpiAttributes, platformIconPath} from '../../../app.constants';
import {InfluencerDetail, InfluencerIgStoryLoadingStatus, InfluencerPost} from '../../../../models/influencer';
import {Subscription} from 'rxjs/Subscription';
import {concatMap, filter, take} from 'rxjs/operators';
import {timer} from 'rxjs/observable/timer';
import {AchooPopupComponent} from '../../../core/achoo-popup/achoo-popup.component';
import {ModalService} from '../../../services/modal/modal.service';
import {TranslateService} from '@ngx-translate/core';
import {UserService} from '../../../services/user.service';
import {PdfExportService} from '../../../services/export-pdf/pdf-export.service';
import {DateUtils} from '../../../utils/date.utils';
import {CacheService} from '../../../services/cache/cache.service';
import {
  ActivePages, EventTrackerService, Page, TargetType,
  UserActionType
} from '../../../services/event-tracker/event-tracker.service';
import {ObjectUtils} from '../../../utils/object.utils';
import { InfluencerPostsComponent } from './influencer-posts/influencer-posts.component';
import { RelatedInfluencer } from '../../../../models/influencer/related-influencer';
import { Subject } from 'rxjs/Subject';
import { combineLatest } from 'rxjs/observable/combineLatest';

enum StoryStatus {
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR'
}

enum StoryStatusDetails {
  GOT_STORIES = 'GOT STORIES',
  NO_STORIES = 'NO STORIES',
  TIMEOUT = 'TIMEOUT',
  SERVER_ERROR = 'SERVER_ERROR'
}

@Component({
  selector: 'app-influencer-overview',
  templateUrl: './influencer-overview.component.html',
  styleUrls: ['./influencer-overview.component.scss']
})
export class InfluencerOverviewComponent implements OnInit, OnDestroy {
  @ViewChild('NotificationFetchingStoryError')
  public notificationFetchingStoryError: AchooPopupComponent;
  @ViewChild('NotificationGetNewStorySuccess')
  public notificationGetNewStorySuccess: AchooPopupComponent;
  @ViewChild('NotificationWarningOpenIncompletedStory') warningOpenIncompletedStory: AchooPopupComponent;
  @ViewChild('InfluencerPosts') influencerPosts: InfluencerPostsComponent; // Todo: change
  @Input() influencerId: number;
  @Input() groupId: number;

  influencerPostList: Array<InfluencerPost>;
  public isFetchingInfluencerPostList = true;
  popularTopic: Array<object>;
  isVisibleTopicChart: boolean;
  errorMessage: string;
  noDataAvailabel = false;

  private influencerDetails: InfluencerDetail;
  public isFetchingInfluencerDetails = true;
  private igStoryLoadingSubscription: Subscription;
  private pollingSubscription: Subscription;

  // story message
  readyToShowPost: boolean;
  private _storyErrorMessage: string;
  private _storySuccessMessage: string;
  private _storyIcompleteMessage: string;
  private pollingNewStoryTimeout: any;
  private pollingIncompletedTimeout: any;
  private fetchedNewStoryDone: boolean;
  private startFetchingStoryTime;
  private endFetchingStoryTime;
  // active channel
  private activeChannel;
  private channelSubscription;

  // latest posts
  public latestPosts: InfluencerPost[] = [];
  public isFetchingLatestPosts = true;

  // brand mentions
  public brandMentions = [];

  // sponsored vs organic
  public sponsoredOrganicPercentage = [];

  // related influcencer
  public relatedInfluencers: RelatedInfluencer[];

  private fetchNewIgStoriesSubscription: Subscription;
  private _cachedInfluencerUpdatedSubscription: Subscription;
  private _influencerInitializedSubject = new Subject<any>();

  constructor(
    private influencerService: InfluencerService,
    private eventTracker: EventTrackerService,
    private modalService: ModalService,
    private translate: TranslateService,
    private userService: UserService,
    private pdfExportService: PdfExportService,
    private cacheService: CacheService,
    private route: ActivatedRoute) {

    // update data after check credit availability from calling `GET /influencer`
    this._cachedInfluencerUpdatedSubscription = combineLatest([
      influencerService.cachedInfluencerUpdated,
      this._influencerInitializedSubject
    ]).subscribe(([inf]) => {
      if (Number(inf.influencerId) !== Number(this.influencerId)) {
        return;
      }

      this.brandMentions = inf.brandMentions;
      this.relatedInfluencers = inf.relatedInfluencers;
      this.sponsoredOrganicPercentage = inf.sponsoredOrganicPercentage;
    });
  }

  ngOnInit() {
    this.isVisibleTopicChart = false;
    this.loadInfluencerDetails(this.influencerId);
    this.getInfluencerPosts(this.influencerId);
    this.getLatestPosts(this.influencerId);
    // handle for IG story
    if (this.influencerService.influencerDetails) {
      // segment track event
      this.eventTracker.trackUserAction(UserActionType.VIEW, Page.INFLUENCER_OVERVIEW, {
        id: this.influencerService.influencerDetails.id,
        name: this.influencerService.influencerDetails.name,
        type: TargetType.INFLUENCER
      }, undefined, undefined, {
        type: ActivePages.INFLUENCER_OVERVIEW,
        name: this.influencerService.influencerDetails.name,
        id: this.influencerService.influencerDetails.id
      });
    }
    this.route.queryParams.subscribe(params => {
      this.groupId = params.group_id;
    });
    this.fetchedNewStoryDone = false;
    this.registerIgStorySubscription();
  }

  private getInfluencerPosts(influencerId: number) {
    const postListParams = {
      limit: CommonConstant.LIMIT_20,
      orderBy: kpiAttributes.engagement
    };
    const cachingKey = `influencer_posts_${influencerId}`;
    this.cacheService.get(cachingKey, this.influencerService.getInfluencerPosts(influencerId, postListParams))
      .subscribe(
        data => {
          this.isFetchingInfluencerPostList = false;

          if (!data) {
            return;
          }
          data.sort((a, b) => b.engagement - a.engagement);
          const postList = data.slice(0, CommonConstant.LIMIT_20);
          postList.forEach(item => {
            if (item.title) {
              item.title  = item.title.replace(/\uFFFD/g, ''); // remove unknown character
              item.title = item.title.substring(0, 100) + '...'; // get 100 characters
            }
          });
          this.influencerPostList = postList;
          this.preloadingPostImage(postList, () => {
            this.readyToShowPost = true;
            // don't fetch new stories when exporting pdf or freemium or basic user
            if (this.userService.isFreeUser() || this.pdfExportService.exportingPdf) {
              return;
            }
            const cachedNewStory = this.influencerService.getLatestNewStory();
            if (!cachedNewStory || cachedNewStory.influencerId !== influencerId) {
              const igChannelArr = this.filterIGChannel();
              if (igChannelArr && igChannelArr.length > 0) {
                this.startFetchingStoryTime = new Date().getTime();
                this.fetchIgNewStories();
              }
            }
          });
        },
        (error) => {
          this.errorMessage = error;
          this.isFetchingInfluencerPostList = false;
        }
      );
  }

  private preloadingPostImage(postList: InfluencerPost[], allImagesLoadedCallback) {
    let loadedCounter = 0;
    const toBeLoadedNumber = postList.length;
    if (toBeLoadedNumber === 0) {
      allImagesLoadedCallback();
    } else {
      postList.forEach(post => {
        preloadImage(post.imageUrl, () => {
          loadedCounter++;
          if (loadedCounter === toBeLoadedNumber) {
            console.log('all images loaded : ' + loadedCounter);
            allImagesLoadedCallback();
          }
        });
      });
    }

    function preloadImage(url, anImageLoadedCallback) {
      const img = new Image();
      img.src = url;
      img.onload = anImageLoadedCallback;
      img.onerror = anImageLoadedCallback;
    }
  }

  public onChangeChannel(channel) {
    this.influencerService.updateActiveChannel(channel);
  }

  /**
   * Subscribe ig story event, update UI anytime event fire
   */
  private registerIgStorySubscription() {
    this.igStoryLoadingSubscription = this.influencerService.loadingNewStoriesEvent.subscribe(newStatus => {
      if (newStatus === InfluencerIgStoryLoadingStatus.DONE) {
        const presentingPostUrlHashes = this.influencerPostList.map(post => post.urlHash);
        // get new stories which not presented yet
        const newStoriesToPresent = this.influencerService.getNewIgStories()
          .filter(newStory => presentingPostUrlHashes.indexOf(newStory.urlHash) < 0);
        // get new stories which are presenting
        const newStoriesPresenting = this.influencerService.getNewIgStories()
          .filter(newStory => presentingPostUrlHashes.indexOf(newStory.urlHash) >= 0);
        // transform new stories before present in post list
        const newStories: InfluencerPost[] = this.influencerService.transformDiscoverPostDataIntoInfluencerPost(
          newStoriesToPresent,
          false
        );
        // update data of existing story
        this.updateDataOfExistingStory(newStoriesPresenting);
        // clear polling timeout for new stories
        clearTimeout(this.pollingNewStoryTimeout);
        // start timeout for polling incompleted stories after 3 minutes
        this.stopPollingIncompletedStory(180000);
        // not show notification when new stories have been done
        if (this.fetchedNewStoryDone) {
          return;
        }
        this.fetchedNewStoryDone = true;
        this.endFetchingStoryTime = new Date().getTime();
        const executionTime = (this.endFetchingStoryTime - this.startFetchingStoryTime) / 1000;
        if (newStories.length > 0) {
          // prepend to mansory list
          if (this.influencerPosts && this.influencerPosts.mansoryPostListContainer) {
            this.influencerPosts.mansoryPostListContainer.prependItems(newStories);
          }
          // show notification
          if (newStories.length > 1) {
            this.showStorySuccessNotification(
              this.translate.instant('story.success.message_multiple', {
                numOfStory: newStories.length
              })
            );
          } else {
            this.showStorySuccessNotification(this.translate.instant('story.success.message_single'));
          }
          // report to Segment
          this.trackStoryEventOnSegement(
            this.influencerDetails.name, this.influencerDetails.id,
            StoryStatus.SUCCESS, StoryStatusDetails.GOT_STORIES,
            newStories.length, executionTime
          );
        } else {
          this.showStorySuccessNotification(
            this.translate.instant('story.success.message_no_story')
          );
          this.trackStoryEventOnSegement(
            this.influencerDetails.name, this.influencerDetails.id,
            StoryStatus.SUCCESS, StoryStatusDetails.NO_STORIES,
            0, executionTime
          );
        }
      }
    });
  }

  /**
   * fetch details of influencer
   * @param influencerId
   */
  private loadInfluencerDetails(influencerId) {
    this.cacheService.get(`influencer_${this.influencerId}`, this.influencerService.getInfluencer(influencerId)).subscribe((data) => {
      const influencer: InfluencerDetail = data;
      this.influencerDetails = influencer;
      this.relatedInfluencers = influencer.relatedInfluencers;
      this.brandMentions = influencer.brandMentions;
      this.sponsoredOrganicPercentage = influencer.sponsoredOrganicPercentage;

      const popularTopic = new Array<object>();
      const topMention = new Array<object>();

      if (influencer.topTopics && Array.isArray(influencer.topTopics)) {
        influencer.topTopics.forEach (topic => {
          if (topic.name && topic.name !== 'NA' && topic.name !== 'Other' && Math.round(topic.percent * 1000) / 10 !== 0) {
            popularTopic.push({ name: topic.name, y: topic.percent });
          }
        });
      }
      this.popularTopic = popularTopic;

      if (influencer.topMentions && Array.isArray(influencer.topMentions)) {
        influencer.topMentions.forEach(mention => {
          topMention.push({
            keyword: mention.mention,
            number: mention.count,
          });
        });
      }

      this.isVisibleTopicChart = influencer.topTopics != null && influencer.topTopics.length > 0;
      this.noDataAvailabel = this.popularTopic.length === 0;
      this.isFetchingInfluencerDetails = false;
      this._influencerInitializedSubject.next(null);
    }, (error) => {
      this.isFetchingInfluencerDetails = false;
      this.errorMessage = error;
    });
  }

  private updateDataOfExistingStory(newStoriesPresenting) {
    newStoriesPresenting.forEach(ns => {
      if (ns.completed) {
        const presentingStory = this.influencerPostList.find(ip => ip.urlHash === ns.urlHash);
        presentingStory.completed = true;
        presentingStory.reach = ns.reach;
        presentingStory.impact = ns.impact * 100;
        presentingStory.engagement = ns.engagement;
        presentingStory.url = ns.fullUrl;
      }
    });
  }

  ngOnDestroy() {
    if (this.igStoryLoadingSubscription) {
      this.igStoryLoadingSubscription.unsubscribe();
    }
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
    }
    if (this.pollingNewStoryTimeout) {
      clearTimeout(this.pollingNewStoryTimeout);
    }
    if (this.pollingIncompletedTimeout) {
      clearTimeout(this.pollingIncompletedTimeout);
    }
    if (this.channelSubscription) {
      this.channelSubscription.unsubscribe();
    }
    if (this.fetchNewIgStoriesSubscription) {
      this.fetchNewIgStoriesSubscription.unsubscribe();
    }
    this._cachedInfluencerUpdatedSubscription.unsubscribe();
    this.modalService.close();
  }

  fetchIgNewStories() {
    const lastSeenDate = this.extractTheLastSeenDate();
    this.fetchNewIgStoriesSubscription = this.influencerService.fetchNewIgStories(this.influencerId, lastSeenDate)
      .subscribe((res) => {
        if (res) {
          this.pollNewIgStoriesUntilFinish(this.influencerId, res.id, lastSeenDate);
          this.stopPollingAfterIntervalTime(30000);
        }
      }, (err) => {
        if (err.error.data === 'missing_source_id' || err.error.data === 'not_found_channel') {
          this.showStoryErrorNotification('story.error.missing_source_id');
        } else {
          this.showStoryErrorNotification('story.error.server_error');
        }
        this.trackStoryEventOnSegement(
          this.influencerDetails.name, this.influencerDetails.id,
          StoryStatus.ERROR, StoryStatusDetails.SERVER_ERROR
        );
      });
  }

  private extractTheLastSeenDate() {
    return Array.isArray(this.influencerPostList) && this.influencerPostList.length > 0 ? this.influencerPostList[0].date : null;
  }

  stopPollingAfterIntervalTime(timeout) {
    this.pollingNewStoryTimeout = setTimeout(() => {
      if (this.pollingSubscription) {
        this.pollingSubscription.unsubscribe();
        this.influencerService.updateNewIgStoryStatus(InfluencerIgStoryLoadingStatus.ERROR);
        this.showStoryErrorNotification('story.error.server_error');
        this.trackStoryEventOnSegement(
          this.influencerDetails.name, this.influencerDetails.id,
          StoryStatus.ERROR, StoryStatusDetails.TIMEOUT
        );
      }
    }, timeout);
  }

  stopPollingIncompletedStory(timeout) {
    this.pollingIncompletedTimeout = setTimeout(() => {
      if (this.pollingSubscription) {
        this.pollingSubscription.unsubscribe();
      }
    }, timeout);
  }

  private checkingPollingFinish(responseData): boolean {
    if (responseData.status === InfluencerIgStoryLoadingStatus.ERROR) {
      return true;
    }
    const numOfIncompleted = responseData.newIgStories.filter(s => !s.completed).length;
    const isFinished = responseData.status === InfluencerIgStoryLoadingStatus.DONE &&
      numOfIncompleted === 0;
    if (responseData.status === InfluencerIgStoryLoadingStatus.DONE) {
      console.log(`Done but still have ${numOfIncompleted} NOT completed`);
    }
    return isFinished;
  }

  private pollNewIgStoriesUntilFinish(influencerId, loaderId, lastSeenDate) {
    this.pollingSubscription = timer(0, 5000)
      .pipe(concatMap(() => this.influencerService.fetchNewIgStories(influencerId, lastSeenDate, loaderId)))
      .pipe(filter(data => data && this.checkingPollingFinish(data)))
      .pipe(take(1))
      .subscribe((res) => {
        if (res.status === InfluencerIgStoryLoadingStatus.ERROR) {
          this.showStoryErrorNotification('story.error.server_error');
          this.trackStoryEventOnSegement(
            this.influencerDetails.name, this.influencerDetails.id,
            StoryStatus.ERROR, StoryStatusDetails.SERVER_ERROR
          );
        }
      });
  }

  showStoryErrorNotification(errorMsgKey) {
    if (this._storyErrorMessage !== errorMsgKey) {
      this._storyErrorMessage = errorMsgKey;
      this.modalService.open(this.notificationFetchingStoryError, this.closeNotification.bind(this), true, 30000);
    }
  }

  showStorySuccessNotification(successMsg) {
    this._storySuccessMessage = successMsg;
    this.modalService.open(this.notificationGetNewStorySuccess, this.closeNotification.bind(this), true, 30);
  }

  private trackStoryEventOnSegement(influencerName, influencerId, status, details,
                                    storyCount?: number, fetchingTime?: number) {
    this.eventTracker.trackUserAction(UserActionType.ACTION, Page.INFLUENCER_DETAILS, {
      id: influencerId,
      name: influencerName,
      type: Page.IG_STORY
    }, {
      action: UserActionType.FETCH,
      action_status: status,
      action_status_details: details,
      action_stories_count: storyCount,
      action_stories_timing: fetchingTime
    });
  }

  openIncompletedStoryWarning(post) {
    const hourDiff = DateUtils.computeTimeDiffInHour(new Date(post.date), new Date());
    if (hourDiff > 24) {
      this._storyIcompleteMessage = this.translate.instant('story.error.incompleted_over_24h');
    } else {
      this._storyIcompleteMessage = this.translate.instant('story.error.incompleted');
    }
    this.modalService.open(this.warningOpenIncompletedStory, null, false);
  }

  get storyErrorMessage() {
    return this._storyErrorMessage;
  }

  get storySuccessMessage() {
    return this._storySuccessMessage;
  }

  get storyIncompleteMessage() {
    return this._storyIcompleteMessage;
  }

  get isExportingPdf() {
    return this.pdfExportService.exportingPdf;
  }

  closeNotification() {
    this._storyErrorMessage = null;
    this._storySuccessMessage = null;
    this.modalService.close();
  }

  private filterIGChannel() {
    if (this.influencerDetails && this.influencerDetails.channels && this.influencerDetails.channels.length > 0) {
      return this.influencerDetails.channels.filter(item => item.platform.id === platformIconPath.instagram.id);
    }
    return [];
  }

  //#region Latest Posts

  /**
   * fetch post list of influencer in 30 days based on channel
   * @param influencerId
   * @param activeChannel
   */
  private getLatestPosts(influencerId): void {
    const postListParams = {
      limit: CommonConstant.LIMIT_INFLUENCER_POST_OVERVIEW,
      period: CommonConstant.PERIOD_30_DAYS
    };
    const cachingKey = `influencer_latest_posts_${influencerId}`;
    // get post list by channel
    this.cacheService.get(cachingKey,
      this.influencerService.getInfluencerPosts(influencerId, postListParams))
      .subscribe((res) => {
        this.latestPosts = res.sort((a, b) => b.date - a.date).slice(0, CommonConstant.LIMIT_INFLUENCER_POST_OVERVIEW);
        this.isFetchingLatestPosts = false;
      }, (err) => {
        this.latestPosts = [];
        this.isFetchingLatestPosts = false;
      });
  }

  //#endregion
}



// WEBPACK FOOTER //
// ./src/app/main/influencer/influencer-overview/influencer-overview.component.ts